import React, { useState, useEffect } from 'react';
import { func } from 'prop-types';
import { Button, Spinner } from 'ui-component-library';
import * as yup from 'yup';

import {
  FormHeader,
  TopRowContainer,
  DriverLocationInputContainer,
  SubmitButtonContainer,
  LoadingContainer,
} from './action-manual-tracking.style';
import AddressBookLookup from '../../../address-book-lookup';
import { updateManualTracking } from '../../../../../../utils/api/load';
import mapAddress from '../../../../../../utils/mappers/address-mapper';
import {
  showSuccessMessage, showErrorMessage, validate, logger,
} from '../../../../../../utils/common';
import CommentBox from '../../../comments-box';
import loadShape from '../../../../../../shapes/load';
import { formatErrorResponse } from '../../../../../../utils/mappers';
import { COMMENTS_CHARACTER_LIMIT } from '../../../../../../constants';
import { getLatLong } from '../../../../../../utils/api/places';

// Validation Schema
export const schema = yup.object().shape({
  driverCity: yup.string().required(),
  driverState: yup.string().required(),
  comments: yup.string().optional()
    .nullable()
    .max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less that ${COMMENTS_CHARACTER_LIMIT}`),
});

/**
 * Manual Tracking action form
 * @param {object} params - Contains props for this component
 * @param {object} load - Load data to display
 * @param {function} params.refetchLoad - Function to reload load summary in parent
 * @return {element} - The manual tracking form
 */
const ManualTrackingAction = ({ load, refetchLoad }) => {
  // State hooks
  const [driverLocation, setDriverLocation] = useState(null);
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Function to clear the current state of the form
   * @return {void}
   */
  const resetState = () => {
    setDriverLocation(null);
    setComments(null);
  };

  useEffect(() => {
    resetState();
  }, [load]);

  /**
  * Checks if the form is valid
  * @return {boolean}
  */
  const checkValid = () => {
    const addressInfo = mapAddress(driverLocation ? driverLocation.value : null);
    const payload = {
      note: comments,
      driverCity: addressInfo.city || '',
      driverState: addressInfo.state || '',
    };
    const valid = validate(payload, schema);
    return valid;
  };

  useEffect(() => {
    resetState();
  }, [load]);

  /**
   * Handles when the driver location has changed
   * @param {object} event - Change event
   * @return {void}
   */
  const handleDriverLocationChanged = (event) => {
    setDriverLocation(event);
  };

  /**
   * Handles when comments change
   * @param {object} event - Change event
   * @return {void}
   */
  const handleCommentsChanged = (event) => {
    setComments(event.target.value);
  };

  /**
   * Handles submitting the manual track
   * form
   * @return {void}
   */
  const handleSubmit = async () => {
    const valid = checkValid();
    if (valid) {
      const addressInfo = mapAddress(driverLocation ? driverLocation.value : null);
      const geocode = await getLatLong(`${addressInfo?.city}, ${addressInfo?.state}`);
      const lat = geocode[0]?.geometry.location.lat();
      const long = geocode[0]?.geometry.location.lng();

      const payload = {
        note: comments || '',
        driverCity: addressInfo.city || '',
        driverState: addressInfo.state || '',
        latitude: lat,
        longitude: long,
      };

      setLoading(true);
      updateManualTracking({
        ...payload,
        loadId: load.loadId,
        billOfLadingNumber: load.billOfLadingNumber,
      }).then(() => {
        showSuccessMessage(`Location manually tracked on Load #${load.billOfLadingNumber}. It may take a few moments for this information to update.`);
        refetchLoad();
        resetState();
      }).catch((error) => {
        logger.error(formatErrorResponse(error));
        showErrorMessage('Something went wrong. The service is currently unavailable. Please try again later.');
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  if (loading) {
    return (
      <div data-testid="Manual Tracking Action">
        <FormHeader>Track Manual Event</FormHeader>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </div>
    );
  }

  // Check if the input is valid on every re-render
  const valid = checkValid();
  return (
    <div data-testid="Manual Tracking Action">
      <FormHeader>Track Manual Event</FormHeader>
      <TopRowContainer>
        <DriverLocationInputContainer>
          <AddressBookLookup
            label="*Driver Location"
            placeholder="Add City or State"
            selectedAddress={driverLocation || null}
            onAddressChanged={handleDriverLocationChanged}
          />
        </DriverLocationInputContainer>
      </TopRowContainer>
      <CommentBox
        comment={comments}
        onCommentChanged={handleCommentsChanged}
        placeholder="Include helpful details related to the location track."
        characterLimit={COMMENTS_CHARACTER_LIMIT}
      />
      <SubmitButtonContainer>
        <Button onClick={handleSubmit} text="Submit" isDisabled={!valid} />
      </SubmitButtonContainer>
    </div>
  );
};

ManualTrackingAction.propTypes = {
  load: loadShape.isRequired,
  refetchLoad: func,
};

ManualTrackingAction.defaultProps = {
  refetchLoad: () => {},
};

export default ManualTrackingAction;
